/**
 * One language's announcements, on the tab that belongs to it.
 *
 * Built from `NoticeVariantFields` - the same editor the base wording uses -
 * rather than from `NoticeDirectionFields`, and the difference is not cosmetic.
 * That component is bound to a `NoticeDirectionSettings` object and builds its
 * slot ids from a template literal; a translation has neither. It has a flat map
 * of slot to wording, and one switch that means something different from the
 * one over there: the direction switch decides whether an announcement plays at
 * all, while this one only decides whether it plays IN THIS LANGUAGE. Off here
 * is not silence - it is the base wording, and every sentence and recording on
 * the tab stays exactly where it is.
 *
 * WHAT AN OPERATOR SEES THAT THEY DO NOT SEE ON THE BASE TAB. The base sentence
 * is printed above each field, greyed. Comparing recorded audio is impossible
 * and comparing scripts is exact, so the only defence against a Russian
 * announcement that quietly stops matching the Estonian one it was translated
 * from is a human noticing - and they can only notice what is on the screen.
 *
 * ⚠ NINE ANNOUNCEMENTS, EACH NAMED, EACH SHUT UNTIL IT IS OPENED. This tab held
 * two blocks when it was written and now holds nine, and for one release in
 * between it rendered them as one flat run whose heading was a two-way ternary:
 * the first block said "when the call is recorded" and the other EIGHT all said
 * "when the call is not recorded but the AI listens". Nine near-identical forms,
 * eight of them mislabelled, roughly four thousand pixels tall, with no way to
 * tell which sentence went where. Three things fix that and all three are load
 * bearing: every slot names itself, the slots come in runs
 * (`NOTICE_SLOT_GROUPS`), and a block that has nothing written in it opens only
 * when somebody asks for it.
 *
 * Closed is not hidden. Each row states whether it holds wording before it is
 * opened, so "what is still to translate" is answered by scrolling the summary
 * column rather than by opening nine forms - which is exactly the question
 * somebody arrives on this tab with.
 *
 * There is no "back to the shipped recording" button here, and that is
 * deliberate rather than missing: the four recordings committed with the API
 * are Estonian, and offering one under a Russian tab would put an Estonian
 * sentence behind a Russian label.
 */
import { useState } from "react";
import { ChevronRight } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import type {
  NoticeClipInfo,
  NoticeLocale,
  NoticeLocaleOverlay,
  NoticeSlot,
  NoticeVariant,
} from "../../../../lib/calls";
import { FieldGroup, Note, ToggleRow } from "../panel/fields";
import { NoticeVariantFields } from "./NoticeVariantFields";
import {
  NOTICE_SLOT_GROUPS,
  TRANSLATABLE_SLOTS,
  localeReachesCalls,
  overlayValue,
} from "./noticeLocales";

/** Is there anything in this slot's translation at all? */
function slotWritten(value: NoticeVariant): boolean {
  return Boolean(value.audioUrl.trim() || value.text.trim());
}

/**
 * One announcement, shut until it is asked for.
 *
 * Open on its own when a translation is already there, because a block holding
 * words is one somebody came here to read; empty ones stay shut and cost a
 * single row each. The state lives per row rather than in the parent: opening
 * the voicemail prompt is not a reason to close the one above it, and a tab
 * with one shared "open slot" would fight the operator working down the list.
 */
function SlotBlock({
  slot,
  locale,
  title,
  live,
  baseText,
  value,
  onChange,
  savedClip,
}: {
  slot: NoticeSlot;
  locale: NoticeLocale;
  title: string;
  /** Marked as the one the account's recording switch actually plays. */
  live: boolean;
  /** The same announcement's base wording, for comparing scripts. */
  baseText: string;
  value: NoticeVariant;
  onChange: (next: NoticeVariant) => void;
  savedClip?: NoticeClipInfo;
}) {
  const t = useT();
  const written = slotWritten(value);
  const [open, setOpen] = useState(written);

  return (
    <div className="rounded-[20px] border border-[var(--crm-hair)]">
      {/* A button, not a `summary` inside `details`: the row carries a state
          word on the right that has to re-render when the field below changes,
          and the native element would keep its own open state beside React's. */}
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-4 py-3 text-left"
      >
        <ChevronRight
          className={`size-3.5 shrink-0 transition-transform ${open ? "rotate-90" : ""}`}
          aria-hidden
        />
        <span className="crm-lab !mt-0 flex-1">
          {title}
          {live ? ` - ${t("settings.notice.inUse")}` : ""}
        </span>
        {/* The one fact worth having without opening the block. Stated in words
            rather than as a dot, because "translated" and "still on the base
            wording" are the two answers this tab exists to give. */}
        <span className="crm-sub shrink-0 text-[11.5px]">
          {t(written ? "settings.notice.slotWritten" : "settings.notice.slotEmpty")}
        </span>
      </button>

      {open ? (
        <div className="space-y-3 border-t border-[var(--crm-hair)] p-4">
          {baseText ? (
            <p className="crm-mut text-[12px] leading-snug">
              {t("settings.notice.baseWording")}: <span className="italic">{baseText}</span>
            </p>
          ) : null}
          <NoticeVariantFields
            slot={slot}
            locale={locale}
            value={value}
            onChange={onChange}
            savedClip={savedClip}
            // No shipped recording behind a translation - see the file header.
            builtInAudio=""
          />
        </div>
      ) : null}
    </div>
  );
}

export function NoticeLocaleFields({
  locale,
  overlay,
  base,
  onChange,
  enabled,
  onEnabledChange,
  record,
  clips,
  needsClip,
}: {
  locale: NoticeLocale;
  overlay: NoticeLocaleOverlay | undefined;
  /** The base wording for each slot, shown beside the translation of it. */
  base: Partial<Record<NoticeSlot, NoticeVariant>>;
  onChange: (slot: NoticeSlot, next: NoticeVariant) => void;
  /** Is this language in use? Off keeps every word and reaches no call. */
  enabled: boolean;
  onEnabledChange: (next: boolean) => void;
  /** The account's recording switch, which decides which wording is the live one. */
  record: boolean;
  clips: Partial<Record<string, NoticeClipInfo>>;
  /** The server's answer: does anything on this tab still need a recording? */
  needsClip: boolean;
}) {
  const t = useT();
  // Only the outbound pair has a live half - the recording switch is what picks
  // between "recorded" and "processed", and the other seven announcements are
  // not a pair at all.
  const live: NoticeSlot = record ? "notice.outbound.recorded" : "notice.outbound.processed";
  const written = TRANSLATABLE_SLOTS.filter((slot) => slotWritten(overlayValue(overlay, slot))).length;

  return (
    <FieldGroup title={t("settings.notice.localeGroup", { language: t(`settings.notice.lang.${locale}`) })}>
      <Note tone="info">{t("settings.notice.localeAbout")}</Note>

      {/* The switch first, above the fields it governs, so somebody arriving to
          turn a language off does not have to scroll past the sentences they
          are trying not to delete. */}
      <ToggleRow
        label={t("settings.notice.localeEnable")}
        hint={t("settings.notice.localeEnableHint")}
        checked={enabled}
        onChange={onEnabledChange}
      />

      {/* Said while the fields are still visible and still editable: switching
          a language off is not the same as clearing it, and the wording stays
          exactly where it is. */}
      {!enabled ? <Note tone="warn">{t("settings.notice.localePaused")}</Note> : null}

      {/* The one warning that flips by language. Twilio has a voice for Russian
          and English and none for Estonian, so this stays quiet on a Russian
          tab holding only a sentence and speaks up on an Estonian one. */}
      {needsClip ? <Note tone="warn">{t("settings.notice.localeNeedsClip")}</Note> : null}

      {/* Written, but for the sentence this account's calls do not use. Said
          here rather than left to the dial pad, where the symptom is a language
          that never appears in the picker and no explanation anywhere. */}
      {enabled && !localeReachesCalls(overlay, record) ? (
        <Note tone="warn">{t("settings.notice.localeUnreachable")}</Note>
      ) : null}

      {/* How much of this language exists, before any block is opened. Nine
          announcements is enough that "am I done" stops being answerable by
          looking, and the count is the whole answer. */}
      <Note tone="mut">
        {t("settings.notice.localeProgress", { done: written, total: TRANSLATABLE_SLOTS.length })}
      </Note>

      {NOTICE_SLOT_GROUPS.map((group) => (
        <div key={group.key} className="space-y-2">
          <h5 className="crm-lab !mt-0">{t(`settings.notice.slotGroup.${group.key}`)}</h5>
          {group.slots.map(({ slot, label }) => (
            <SlotBlock
              key={slot}
              slot={slot}
              locale={locale}
              // `label`, not `slot`: a slot id has dots in it and `translate()`
              // walks a key by splitting on them, so the id as a key leaf would
              // print itself on screen.
              title={t(`settings.notice.slot.${label}`)}
              live={slot === live}
              baseText={base[slot]?.text.trim() ?? ""}
              value={overlayValue(overlay, slot)}
              onChange={(next) => onChange(slot, next)}
              savedClip={clips[`${slot}@${locale}`]}
            />
          ))}
        </div>
      ))}
    </FieldGroup>
  );
}
